import { useState } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Disclosure } from '@components/disclosure';
import { IconAppExtraSmall } from '@components/icons/app-extra-small';
import { IconAttachment } from '@components/icons/attachment';
import { IconFlagGB } from '@components/icons/flag-gb';
import { IconFlagIS } from '@components/icons/flag-is';
import { IconForm } from '@components/icons/form';
import { IconList } from '@components/icons/list';
import { IconQuestion } from '@components/icons/question';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { pxToRem } from '@utils/converters';

import { JobLabel } from 'api/labels/requests';
import { Box } from 'components/Box';
import { useStore } from 'models/Provider';
import { IJob, IJobFormSection } from 'models/types';

export function Details({ job }: { job: IJob }) {
  const { t } = useStore();
  const [language, setLanguage] = useState(job.defaultLanguageData?.language ?? 'en');

  return (
    <Stack space={6}>
      <Box flex justifyContent="space-between">
        <Box flex h="40" alignItems="center">
          <Text font="bold18" text={t('job.info.details.header')} />
        </Box>
        {job.languages.length > 1 && (
          <Box w="200px">
            <Select
              onChange={(value) => setLanguage(value)}
              options={[
                {
                  value: 'en',
                  label: (
                    <Box flex alignItems="center">
                      <Box flex alignItems="center" mr="8">
                        <IconFlagGB />
                      </Box>
                      {t('job.info.details.language.english')}
                    </Box>
                  ),
                },
                {
                  value: 'is',
                  label: (
                    <Box flex alignItems="center">
                      <Box flex alignItems="center" mr="8">
                        <IconFlagIS />
                      </Box>
                      {t('job.info.details.language.icelandic')}
                    </Box>
                  ),
                },
              ]}
              defaultValue="en"
              isClearable={false}
            />
          </Box>
        )}
      </Box>
      <Separator />
      <Disclosure
        icon={<IconList color="purple80" />}
        text={t('job.info.details.jobdetails.header')}
        content={[<JobDetailsContent job={job} language={language} />]}
      />
      <Separator />
      <Disclosure
        icon={<IconTextLong color="purple80" />}
        text={t('job.info.details.jobdescription.header')}
        content={[<JobDescriptionContent job={job} language={language} />]}
      />
      <Separator />
      <Disclosure
        icon={<IconForm color="purple80" />}
        text={t('job.info.details.applicationform.header')}
        content={[<ApplicationFormContent job={job} />]}
      />
      <Separator />
      <Disclosure
        icon={<IconQuestion color="purple80" />}
        text={t('job.info.details.questions.header')}
        content={[<QuestionsContent job={job} language={language} />]}
      />
      <Separator />
    </Stack>
  );
}

interface JobProps {
  job: IJob;
}
interface JobAndLanguageProps {
  job: IJob;
  language: string;
}

const JobDetailsContent = observer((props: JobAndLanguageProps) => {
  const { current, t } = useStore();
  const { job, language } = props;
  const selectedLanguage = job.languages.find((job: IJob) => job.language === language);
  return (
    <Columns>
      <div>
        <Text font="semibold14" text={t('job.info.details.jobdetails.jobtitle')} />
        <Text text={selectedLanguage?.title} />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.jobdetails.worktype')} />
        <Text text={job.workTypeDisplay(language)} />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.jobdetails.location')} />
        <Text text={job.locationDisplay(language)} />
      </div>
      {current.info.isEnterprise && (
        <div>
          <Text font="semibold14" text={t('job.info.details.jobdetails.department')} />
          <Text text={job.departmentDisplay(language)} />
        </div>
      )}
      <div>
        <Text font="semibold14" text={t('job.info.details.jobdetails.jobstatus')} />
        <Text text={t(`job.status.${job.status}`)} />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.jobdetails.visibility')} />
        <Text text={t(`job.visibility.${job.visibility}`)} />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.jobdetails.deadline')} />
        <Text text={job.deadlineDisplay} />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.jobdetails.published')} />
        <Text text={job.datePublishedDisplayWithoutPrefix} />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.jobdetails.createdby')} />
        <Text text={job.createdById && job.createdById.name} />
      </div>
      {job.labelsDisplay.length > 0 ? (
        <Stack space={1}>
          <Text font="semibold14" text={t('job.info.details.jobdetails.labels')} />
          <LabelColumns>
            {job.labelsDisplay.map((label: JobLabel) => (
              <Stack orientation="horizontal">
                <Tag
                  key={label.id}
                  size="small"
                  variant="customColor"
                  color={label.color}
                  text={label.title}
                  icon={<IconAppExtraSmall />}
                />
              </Stack>
            ))}
          </LabelColumns>
        </Stack>
      ) : null}
    </Columns>
  );
});

const LabelColumns = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  rowGap: pxToRem(8),
  div: {
    gridColumnStart: 'span 3',
  },
});

const JobDescriptionContent = observer((props: JobAndLanguageProps) => {
  const { t } = useStore();
  const { job, language } = props;
  return (
    <>
      <Text font="semibold14" text={t('job.info.details.jobdescription.shortdescription')} />
      <Text style={{ whiteSpace: 'normal' }}>
        <div
          dangerouslySetInnerHTML={{
            __html: job.languages.find((job: IJob) => job.language === language)
              ?.shortDescriptionHtml,
          }}
        />
      </Text>
      <Text font="semibold14" text={t('job.info.details.jobdescription.longdescription')} />
      <Text style={{ whiteSpace: 'normal' }}>
        <div
          dangerouslySetInnerHTML={{
            __html: job.languages.find((job: IJob) => job.language === language)?.description,
          }}
        />
      </Text>
    </>
  );
});

const ApplicationFormContent = observer((props: JobProps) => {
  const { t } = useStore();
  const { job } = props;
  return (
    <Columns>
      <div>
        <Text font="semibold14" text={t('job.info.details.applicationform.name')} />
        <Text color="blue100" text={t('job.info.details.applicationform.options.alwaysrequired')} />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.applicationform.email')} />
        <Text color="blue100" text={t('job.info.details.applicationform.options.alwaysrequired')} />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.applicationform.ssn')} />
        <Text
          color={job.requireSsn ? 'green100' : job.includeSsn ? 'orange100' : 'purple60'}
          text={
            job.requireSsn
              ? t('job.info.details.applicationform.options.required')
              : job.includeSsn
              ? t('job.info.details.applicationform.options.optional')
              : t('job.info.details.applicationform.options.off')
          }
        />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.applicationform.phone')} />
        <Text
          color={job.requirePhone ? 'green100' : job.includePhone ? 'orange100' : 'purple60'}
          text={
            job.requirePhone
              ? t('job.info.details.applicationform.options.required')
              : job.includePhone
              ? t('job.info.details.applicationform.options.optional')
              : t('job.info.details.applicationform.options.off')
          }
        />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.applicationform.cv')} />
        <Text
          color={job.requireCv ? 'green100' : job.includeCv ? 'orange100' : 'purple60'}
          text={
            job.requireCv
              ? t('job.info.details.applicationform.options.required')
              : job.includeCv
              ? t('job.info.details.applicationform.options.optional')
              : t('job.info.details.applicationform.options.off')
          }
        />
      </div>
      <div>
        <Text font="semibold14" text={t('job.info.details.applicationform.coverletter')} />
        <Text
          color={
            job.requireCoverLetter ? 'green100' : job.includeCoverLetter ? 'orange100' : 'purple60'
          }
          text={
            job.requireCoverLetter
              ? t('job.info.details.applicationform.options.required')
              : job.includeCoverLetter
              ? t('job.info.details.applicationform.options.optional')
              : t('job.info.details.applicationform.options.off')
          }
        />
      </div>
    </Columns>
  );
});

const QuestionsContent = observer((props: JobAndLanguageProps) => {
  const { t } = useStore();
  const { job, language } = props;
  const sections = job.languages.find((job: IJob) => job.language === language)?.form?.sections;
  return (
    <div>
      {sections &&
        sections.map((section: IJobFormSection) =>
          section.questions.map((question) => {
            return (
              <Box alignItems="center" key={question.id} mb="16">
                <Box flex alignItems="center">
                  <Stack space={1} orientation="horizontal">
                    {questionIcon({ type: question.type })}
                    <Text font="semibold14" text={question.title} />
                  </Stack>
                </Box>
                <Text
                  color={
                    question.required ? 'green100' : question.hidden ? 'purple60' : 'orange100'
                  }
                  text={
                    question.required
                      ? t('job.info.details.applicationform.options.required')
                      : question.hidden
                      ? t('job.info.details.applicationform.options.off')
                      : t('job.info.details.applicationform.options.optional')
                  }
                />
              </Box>
            );
          })
        )}
    </div>
  );
});

function questionIcon({ type }: { type: string }) {
  switch (type) {
    case 'multi_line':
      return <IconTextLong color="purple100" />;
    case 'dropdown':
      return <IconSelectSingle color="purple100" />;
    case 'multidropdown':
      return <IconSelectMultiple color="purple100" />;
    case 'attachment':
      return <IconAttachment color="purple100" />;
    case 'one_line':
    default:
      return <IconTextShort color="purple100" />;
  }
}

const Columns = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  rowGap: '1rem',
  div: {
    gridColumnStart: 'span 3',
  },
});
